import { useState, useEffect } from 'react';
import { Factura } from '../types';
import type { ImpresionPayload } from '../services/facturasService';
import {
  fetchFacturas,
  anularFactura as anularFacturaService,
  imprimirFactura as imprimirFacturaService
} from '../services/facturasService';

interface Filtros {
  estado?: string;
  fechaInicio?: Date | string;
  fechaFin?: Date | string;
  page?: number;
  limit?: number;
  search?: string;
}

interface Estadisticas {
  total: number;
  pendientes: number;
  pagadas: number;
  anuladas: number;
}

interface PaginationInfo {
  page: number;
  limit: number;
  totalPages: number;
  totalCount: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export const useFacturas = () => {
  const [facturas, setFacturas] = useState<Factura[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtrosActuales, setFiltrosActuales] = useState<Filtros>({ page: 1, limit: 100 });
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 100,
    totalPages: 0,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [estadisticas, setEstadisticas] = useState<Estadisticas>({
    total: 0,
    pendientes: 0,
    pagadas: 0,
    anuladas: 0
  });

  const calcularEstadisticas = (facturas: Factura[]) => {
    return {
      total: facturas.length,
      pendientes: facturas.filter(f => f.estado === 'pendiente').length,
      pagadas: facturas.filter(f => f.estado === 'pagada').length,
      anuladas: facturas.filter(f => f.estado === 'anulada').length
    };
  };

  const loadFacturas = async (filtros?: Filtros) => {
    try {
      setLoading(true);
      const filtrosAplicar = { ...filtrosActuales, ...filtros };
      setFiltrosActuales(filtrosAplicar);

      // Construir query params para los filtros
      const params = new URLSearchParams();

      // Pagination params
      params.append('page', (filtrosAplicar.page || 1).toString());
      params.append('limit', (filtrosAplicar.limit || 100).toString());

      // Filter params
      if (filtrosAplicar.estado) {
        params.append('estado', filtrosAplicar.estado);
      }
      if (filtrosAplicar.search) {
        params.append('search', filtrosAplicar.search);
      }
      if (filtrosAplicar.fechaInicio) {
        params.append('fechaInicio', new Date(filtrosAplicar.fechaInicio).toISOString());
      }
      if (filtrosAplicar.fechaFin) {
        params.append('fechaFin', new Date(filtrosAplicar.fechaFin).toISOString());
      }

      // Agregar parámetro único para evitar cache
      params.append('_ts', Date.now().toString());
      const response = await fetchFacturas(params.toString());
      setFacturas(response.data);

      if (response.pagination) {
        setPagination(response.pagination);
      }

      setEstadisticas(calcularEstadisticas(response.data));
      setError(null);
      return response.data;
    } catch (err) {
      setError('Error al cargar facturas');
      console.error('Error al cargar facturas:', err);
    } finally {
      setLoading(false);
    }
  };

  const anularFactura = async (id: string, motivo: string) => {
    await anularFacturaService(id, motivo);
    await loadFacturas(); // Recargar facturas con los filtros actuales
  };

  // Fase 2: acepta string legado (solo metodoPago) o PagoDetalle completo
  // ({metodoPago, cheque?, montoRecibido?, vuelto?}) — el body lo arma el
  // servicio; acá solo se propaga y se recarga la lista.
  // add-print-without-payment: la unión se ensancha con ImpresionSinPago
  // ({sinPago:true}); la recarga de lista es idéntica en ambos caminos.
  const imprimirFactura = async (id: string, pago: ImpresionPayload) => {
    await imprimirFacturaService(id, pago);
    await loadFacturas();
  };

  const cambiarPagina = (nuevaPagina: number) => {
    loadFacturas({ ...filtrosActuales, page: nuevaPagina });
  };

  const cambiarLimite = (nuevoLimite: number) => {
    loadFacturas({ ...filtrosActuales, limit: nuevoLimite, page: 1 });
  };

  // Cargar facturas inicialmente
  useEffect(() => {
    loadFacturas();
  }, []);

  return {
    facturas,
    loading,
    error,
    estadisticas,
    filtrosActuales,
    pagination,
    loadFacturas,
    anularFactura,
    imprimirFactura,
    cambiarPagina,
    cambiarLimite
  };
};